// ─────────────────────────────────────────────────────────────────
// Design tokens — "friendly consumer" palette (BRO-687)
// Source of truth: docs/design-brief/index.html
// Warm ivory surfaces, sage primary, terracotta + amber supporting,
// coral reserved for destructive signal.
// ─────────────────────────────────────────────────────────────────

export const tokens = {
  // surfaces
  page: "#fbf8f2",
  card: "#ffffff",
  sunken: "#f4efe5",

  // ink
  ink: "#2b2a26",
  inkSoft: "#4d4a42",
  muted: "#6f6a5f",
  faint: "#a19884",

  // lines
  line: "#ece4d2",
  lineStrong: "#d8cfb9",

  // sage (primary accent)
  sage: "#5e7f4e",
  sageDeep: "#3f5b32",
  sageSoft: "#e8efe0",
  sageTint: "#f2f6ec",

  // terracotta (warm secondary)
  terracotta: "#b55e33",
  terracottaLight: "#c96e3f",
  terracottaSoft: "#fbeadd",

  // amber (warning / pending)
  amber: "#c08d2a",
  amberLight: "#d4a64a",
  amberSoft: "#faf1d9",
  amberInk: "#6d5017",

  // coral (destructive / critical)
  coral: "#c55a52",
  coralDeep: "#9b3f38",
  coralSoft: "#fce3e0",

  // slate (cool neutral, used in soak chip)
  slateSoft: "#e5ecf1",
  slateInk: "#3a4a5a",
} as const;

export const radius = {
  xs: "6px",
  sm: "10px",
  md: "14px",
  lg: "18px",
  xl: "24px",
  pill: "999px",
} as const;

export const shadow = {
  xs: "0 1px 2px rgba(60, 42, 20, 0.05)",
  sm: "0 2px 8px rgba(60, 42, 20, 0.06)",
  md: "0 6px 20px rgba(60, 42, 20, 0.08)",
  lg: "0 16px 40px rgba(60, 42, 20, 0.10)",
} as const;

// Utilitarian system stacks. No web fonts — Eric wants the app to feel
// like production tooling, not a consumer / editorial product.
const SYSTEM_SANS = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
const SYSTEM_MONO = `ui-monospace, SFMono-Regular, "SF Mono", Menlo, "Cascadia Code", Consolas, monospace`;

export const fonts = {
  display: SYSTEM_SANS,
  body: SYSTEM_SANS,
  mono: SYSTEM_MONO,
} as const;

// ─────────────────────────────────────────────────────────────────
// Legacy alias shim — views and tests reference these names; we
// remap the values to the new palette so the redesign flows through
// without touching every consumer on day one.
// ─────────────────────────────────────────────────────────────────
export const colors = {
  bg: tokens.page,
  bgRaised: tokens.card,
  bgDeeper: tokens.sunken,

  border: tokens.line,
  borderStrong: tokens.lineStrong,

  text: tokens.ink,
  textBright: tokens.ink,
  textMuted: tokens.inkSoft,
  textDim: tokens.muted,
  textFaint: tokens.faint,

  // Legacy "infrared" now reads as coral — reserved for destructive /
  // low-stock / failure signal only. For delivery accent use
  // `tokens.terracotta` directly.
  infrared: tokens.coral,
  infraredDeep: tokens.coralDeep,

  gold: tokens.amber,
  goldDeep: "#9c7317",

  green: tokens.sage,
  greenDeep: tokens.sageDeep,

  slate: tokens.muted,
  slateDeep: tokens.inkSoft,
} as const;

// Tray data-viz colors survive from the existing brand — they were
// already on-palette for the new direction.
export const trayColors: Record<string, string> = {
  A: "#7a9a65",
  B: "#d4b04a",
  C: "#c47850",
  D: "#9b6a8f",
};

// Crop-keyed colors (BRO-699). Shared trays mix multiple crops; each
// segment of the tray icon needs its own color so a 1/2 pea + 1/4
// sunflower + 1/4 arugula reads at a glance. The four canonical crops
// inherit the existing tray palette so a whole-tray Pea cycle on Tray
// A still looks like Tray A. Brassica varieties get their own hues.
export const cropColors: Record<string, string> = {
  "Pea shoots": "#7a9a65",
  "Sunflower (black oil)": "#d4b04a",
  Sunflower: "#d4b04a",
  "Radish (confetti mix)": "#c47850",
  Radish: "#c47850",
  "Brookfield Brassica Mix": "#9b6a8f",
  "Broccoli (Waltham)": "#6b9a7a",
  "Red Russian Kale": "#b85a6f",
  "Red Acre Cabbage": "#a04a55",
  "Purple Vienna Kohlrabi": "#8a5a9b",
  "Arugula (Slow Bolt)": "#84a55e",
};

// Stable fallback palette for unknown crops — picked from a hash so
// the same crop name always maps to the same color across renders.
const CROP_COLOR_FALLBACK_PALETTE = [
  "#7a9a65",
  "#d4b04a",
  "#c47850",
  "#9b6a8f",
  "#6b9a7a",
  "#b85a6f",
  "#a04a55",
  "#8a5a9b",
  "#84a55e",
];

export function getCropColor(crop: string): string {
  const direct = cropColors[crop];
  if (direct) return direct;
  let hash = 0;
  for (let i = 0; i < crop.length; i += 1) {
    hash = (hash * 31 + crop.charCodeAt(i)) >>> 0;
  }
  return CROP_COLOR_FALLBACK_PALETTE[hash % CROP_COLOR_FALLBACK_PALETTE.length]!;
}

export type EventKind = "seed" | "soak" | "uncover" | "harvest" | "reseed" | "check" | "note";

// Event-kind chip styles — map to brief chip palette. `fg`/`bg` are
// the chip colors; `ring` is the stripe/accent where used.
export const kindStyles: Record<
  EventKind,
  { label: string; fg: string; bg: string; ring: string }
> = {
  seed: { label: "SEED", fg: tokens.amberInk, bg: tokens.amberSoft, ring: tokens.amber },
  reseed: { label: "RESEED", fg: tokens.amberInk, bg: tokens.amberSoft, ring: tokens.amber },
  harvest: {
    label: "HARVEST",
    fg: tokens.terracotta,
    bg: tokens.terracottaSoft,
    ring: tokens.terracotta,
  },
  uncover: {
    label: "UNCOVER",
    fg: tokens.sageDeep,
    bg: tokens.sageSoft,
    ring: tokens.sage,
  },
  soak: { label: "SOAK", fg: tokens.slateInk, bg: tokens.slateSoft, ring: tokens.slateInk },
  check: { label: "CHECK", fg: tokens.inkSoft, bg: tokens.sunken, ring: tokens.lineStrong },
  note: { label: "NOTE", fg: tokens.muted, bg: "transparent", ring: tokens.line },
};

export const typography = {
  mono: fonts.mono,
  serif: fonts.display,
  body: fonts.body,
} as const;

export const TRAY_IDS = ["A", "B", "C", "D"] as const;
export type TrayId = (typeof TRAY_IDS)[number];

export const TRAY_LABELS: Record<TrayId, string> = {
  A: "Pea shoots",
  B: "Sunflower",
  C: "Radish confetti",
  D: "Brassica mix",
};
